import axios, { type AxiosRequestConfig, type AxiosResponse } from '@fs/zion-axios'
import { i18n } from '@fs/zion-locale'
import zionDebug from '@fs/zion-debug'
import placeAPIErrors from './placeApiWarningMessages.json'

const debug = zionDebug('standards:places:apiService')

let cacheBusterCounter = 1

type PlaceAPIError = {
  code: number
  matchWarningText?: string
  paramNames?: string[]
  translationKey: string
  message: string
}

type APIWarning = {
  code?: string
  message?: string
  parameters?: string[]
}

type LogFetchParams = {
  method?: string
  sPath?: string
  sParameters?: string
  sBody?: Record<string, unknown>
  sRspTimeMS?: string
  error?: Record<string, unknown> | string
  status?: string | number
  level?: number
  xRequestId?: string
}

type Jurisdiction = {
  name?: string
  jurisdiction?: Jurisdiction
}

type SendMailAPIInfo = {
  url: string
  origin: string
  recipientEmail: string
}

export function isValidUrL(textval: string): boolean {
  textval = encodeURI(textval)
  const urlregex = /^(ftp|http|https):\/\/[^ "]+$/
  return urlregex.test(textval)
}

function getHttpErrorMessage(httpError: number): string {
  switch (httpError) {
    case 400:
    case 422:
      return i18n.t(
        'http-error-400-422.bad-params',
        "We're sorry. An unexpected error occurred. Please verify your entries and try again later."
      )
    case 404:
      return i18n.t(
        'http-error-404.bad-params',
        "We're sorry. The requested entry was not found. You may need to refresh your page or try again later."
      )
    case 423:
      return i18n.t(
        'http-error-423.locked-resource',
        'One or more fields cannot be modified because the place is Certified.'
      )
    case 500:
      return i18n.t(
        'http-error-500.place-service',
        "We're sorry. An unexpected error occurred. Please try again later."
      )
    case 503:
      return i18n.t(
        'http-error-503.place-service-busy',
        "We're sorry. The FamilySearch Place Service is too busy to process your request right now. Please try again later."
      )
    default:
      return i18n.t('places-app.general-error', 'An error occurred. Please try again later.')
  }
}

function getTranslationParameterConfig({
  paramNames = [],
  parameters,
}: {
  paramNames?: string[]
  parameters?: string[]
}): Record<string, string> {
  if (!parameters?.length || !paramNames.length || parameters?.length !== paramNames?.length) {
    return {}
  }

  const parameterObj = parameters.reduce((paramsObject: Record<string, string>, paramValue: string, index: number) => {
    const paramName = paramNames[index]
    paramsObject[paramName] = paramValue
    return paramsObject
  }, {})

  return parameterObj
}

/**
 * Handles errors from the Place API and returns an appropriate error message.
 *
 * @param err - The error object returned from the API call. It should
 * have a `response` property which contains the HTTP response from the API. The
 * `response` object should have a `status` property (HTTP status code) and a
 * `headers` property. The `headers` property should contain a `warning` field
 * which is a JSON string that represents the warning message from the API.
 *
 * @returns errorMessage - The error message. If an API warning is found,
 * it generates an error message using the `i18n.t` function with the
 * `translationKey`, `message`, and `parameters` from the API warning. If no API
 * warning is found, it uses the HTTP error message.
 *
 */

export function getPlaceAPIErrorMessageFromStatusAndWarnHeader(err: unknown): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const httpErrorMessage = getHttpErrorMessage((err as any)?.response?.status)
  let warning: APIWarning
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    warning = JSON.parse((err as any)?.response?.headers?.warning || null)
  } catch {
    warning = {}
  }

  // WARNING!!! Backend Error codes are not unique.
  // The client must match API code, message in english, and significant parameters.
  const { code, message, parameters } = warning || {}
  const apiWarning = (placeAPIErrors as PlaceAPIError[]).find((apiErr) => {
    return (
      apiErr.code === Number(code) &&
      (!apiErr?.matchWarningText || message?.indexOf(apiErr.matchWarningText) !== -1) &&
      (!apiErr?.paramNames?.length || parameters?.length === apiErr?.paramNames?.length)
    )
  })
  const errorMessage = apiWarning
    ? i18n.t(
        apiWarning.translationKey,
        apiWarning.message,
        getTranslationParameterConfig({ paramNames: apiWarning.paramNames, parameters })
      )
    : httpErrorMessage
  const logMessage = code && !apiWarning ? `Error:${code} Fallback:${errorMessage}` : `Error:${code} ${errorMessage}`
  debug(logMessage)
  return errorMessage
}

export function getBasePlaceServiceUrl(isEditMode = false): string {
  if (isEditMode) {
    return '/service/standards/place/ws-write'
  }
  return '/service/standards/place/ws-ui'
}

export function getBaseDateServiceUrl(isEditMode = false): string {
  if (isEditMode) {
    return '/service/standards/date/ws-write'
  }
  return '/service/standards/date/ws-ui'
}

export function getCacheBuster(): number {
  return Date.now() + cacheBusterCounter++
}

export function logFetch({
  method = 'get',
  sPath,
  sParameters = '',
  sBody = {},
  sRspTimeMS,
  error = {},
  status,
  level = 1,
  xRequestId = '',
}: LogFetchParams = {}): void {
  const logMethod = `method=${method}`
  const logSPath = sPath ? `sPath=${sPath}` : ''
  const logSParameters = sParameters ? `sParameters=${stringify(sParameters)}` : ''
  const logSBody = sBody ? `sBody=${stringify(sBody)}` : ''
  const logSBodyTrimmed = logSBody.length > 32 ? `${logSBody.substring(0, 32)}...} ` : logSBody
  const logSRspTimeMS = sRspTimeMS ? `sRspTimeMS=${sRspTimeMS}` : ''
  const logError = error ? `error=${stringify(error)}` : ''
  const logStatus = status ? `status=${status}` : ''
  const logXRequestId = xRequestId ? `xRequestId=${xRequestId}` : ''

  tempWindowFSRemoteLog(
    `sApp=places ${logMethod} ${logSPath} ${logSParameters} ${logSBodyTrimmed} ${logSRspTimeMS} ${logError} ${logStatus} ${logXRequestId}`,
    level
  )
}

export function getFullParentName(juris: Jurisdiction = {}, separator = ', '): string {
  if (!juris.jurisdiction) return juris.name || ''
  return [juris.name, getFullParentName(juris.jurisdiction, separator)].join(separator)
}

type FetchParams = {
  url: string
  method: string
  body?: unknown
  xRequestId: string | undefined
}

function logSuccess(params: FetchParams, startTime: number, resp: AxiosResponse): void {
  const { url, method, body, xRequestId } = params
  const pathParamSplit = url.split('?')
  const sPath = pathParamSplit[0]
  const sParameters = pathParamSplit[1] ? `?${pathParamSplit[1]}` : undefined
  const sRspTimeMS = `${window.performance.now() - startTime}`
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const status = (resp as any).errors ? 'ERROR' : (resp as any).statusMessage
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const error = (resp as any).errors ? JSON.stringify((resp as any).errors) : undefined
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const level = (resp as any).errors ? 4 : 1
  logFetch({
    method,
    sPath,
    sParameters,
    sBody: (body ?? {}) as Record<string, unknown>,
    sRspTimeMS,
    error,
    status,
    level,
    xRequestId,
  })
}

function logFailure(params: FetchParams, startTime: number, err: unknown): void {
  const { url, method, body, xRequestId } = params
  const pathParamSplit = url.split('?')
  const sPath = pathParamSplit[0]
  const sParameters = pathParamSplit[1] ? `?${pathParamSplit[1]}` : undefined
  const sRspTimeMS = `${window.performance.now() - startTime}`
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const status = (err as any).response ? (err as any).response.status : (err as any).status
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const error = (err as any).message || (err as any).statusMessage
  logFetch({
    method,
    sPath,
    sParameters,
    sBody: (body ?? {}) as Record<string, unknown>,
    sRspTimeMS,
    error,
    status,
    level: 4,
    xRequestId,
  })
}

export async function doFetchGet(
  url: string,
  config?: AxiosRequestConfig,
  // skipSuccessLog: for high-volume callers (e.g. boundary probes) that should only
  // reach Splunk when something goes wrong; failures are always logged
  options?: { skipSuccessLog?: boolean }
): Promise<AxiosResponse> {
  const finalConfig = config ?? { headers: getDefaultHeaders() }
  const xRequestId = finalConfig?.headers?.['X-Request-ID']
  const startTime = window.performance.now()
  const params: FetchParams = { url, method: 'get', xRequestId }
  debug(`apiService.doFetchGet: {url: ${url}, config: ${JSON.stringify(finalConfig)}}`)
  try {
    const resp = await axios.get(url, finalConfig)
    if (!options?.skipSuccessLog) logSuccess(params, startTime, resp)
    return resp
  } catch (err) {
    logFailure(params, startTime, err)
    throw err
  }
}

export async function doFetchPost(url: string, body: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse> {
  const finalConfig = config ?? { headers: getDefaultHeaders() }
  const xRequestId = finalConfig?.headers?.['X-Request-ID']
  const startTime = window.performance.now()
  const params: FetchParams = { url, method: 'post', body, xRequestId }
  try {
    const resp = await axios.post(url, body, finalConfig)
    logSuccess(params, startTime, resp)
    return resp
  } catch (err) {
    logFailure(params, startTime, err)
    throw err
  }
}

export async function doFetchPut(url: string, body: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse> {
  const finalConfig = config ?? { headers: getDefaultHeaders() }
  const xRequestId = finalConfig?.headers?.['X-Request-ID']
  const startTime = window.performance.now()
  const params: FetchParams = { url, method: 'put', body, xRequestId }
  try {
    const resp = await axios.put(url, body, finalConfig)
    logSuccess(params, startTime, resp)
    return resp
  } catch (err) {
    logFailure(params, startTime, err)
    throw err
  }
}

export async function doFetchDelete(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse> {
  const finalConfig = config ?? { headers: getDefaultHeaders() }
  const xRequestId = finalConfig?.headers?.['X-Request-ID']
  const startTime = window.performance.now()
  const params: FetchParams = { url, method: 'del', xRequestId }
  try {
    const resp = await axios.delete(url, finalConfig)

    // Fix for PVT-178.
    // The following status checks are a work-around for the backend API.
    // They should be removed once the places backend error handling story is completed.
    // `https://www5.v1host.com/FH-V1/story.mvc/Summary?oidToken=Story:882650`
    if (resp.status >= 400) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (resp.status === 400) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ;(resp as any).errors = [{ key: 'ERROR_GENERIC' }]
      } else if (resp.status >= 400) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ;(resp as any).errors = [{ key: String(resp.status) }]
      }
    }

    logSuccess(params, startTime, resp)
    return resp
  } catch (err) {
    logFailure(params, startTime, err)
    throw err
  }
}

export async function getDates(queryString: string): Promise<unknown[]> {
  const baseUrl = getBaseDateServiceUrl()
  const interpUrl = `${baseUrl}/dates/interp?text=${queryString}&noCache=true&locale=${i18n.language}`
  const response = await doFetchGet(`${interpUrl}`)

  // debug('API_DATES_DATA:', response.data.dates)
  return response.data.dates
}

let uniqueId = 0

export function getXRequestId(): string {
  const now = Date.now()
  return `${now}-${++uniqueId}-places-react`
}

export function getDefaultHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/standards-places-v2+json',
    Accept: 'application/json',
    'X-Request-ID': getXRequestId(),
  }
}

export function getSendMailAPIInfo(): SendMailAPIInfo {
  const host = window.location.host
  switch (host) {
    case 'www.familysearch.org':
      return {
        url: '/service/messaging/send/api/snippets/controlled',
        origin: 'https://www.familysearch.org',
        recipientEmail: 'placefeedback@familysearch.org', // only valid address for production
      }
    case 'beta.familysearch.org':
    case 'localhost':
    case 'localhost:5000':
      return {
        url: '/service/messaging/send/api/snippets/controlled',
        origin: 'https://beta.familysearch.org',
        recipientEmail: 'thorntonjn@familysearch.org', // whitelisted for testing on beta/localhost
      }
    case 'integration.familysearch.org':
      return {
        url: '/service/messaging/send/api/snippets/controlled',
        origin: 'https://integration.familysearch.org',
        recipientEmail: 'thorntonjn@familysearch.org', // whitelisted for integration
      }
    default:
      throw new Error(`An appropriate URL for messaging/send/api/snippets/controlled could not be found for ${host} !`)
  }
}

function safeStringify(obj: unknown): string {
  try {
    return typeof stringify === 'function' ? stringify(obj) : JSON.stringify(obj)
  } catch (err) {
    try {
      return JSON.stringify({ m: `Failed to serialize data: ${String(err)}`, s: 1 })
    } catch {
      return '{"m":"unserializable log data","s":1}'
    }
  }
}

function isLocalHostLike(loc: Location): boolean {
  const h = loc?.hostname || ''
  return h === 'localhost' || h === '127.0.0.1' || h === '0.0.0.0'
}

/**
 * Log a message to remote with a severity level.
 * @param data         - Information to log to remote.
 * @param severity - Severity level (log = 1, info = 2, warn = 3, error = 4)
 */
export function tempWindowFSRemoteLog(data: string | Record<string, unknown>, severity = 1): void {
  // SSR / non-browser: no-op
  if (typeof window === 'undefined') return

  try {
    const s = Math.min(4, Math.max(1, Number.isFinite(severity) ? severity : 1))

    const logObj = { m: data, s }

    const url = isLocalHostLike(window.location) ? '/splunk-logger' : '/research/places/splunk-logger'

    const payload = safeStringify(logObj)

    // Prefer sendBeacon for fire-and-forget if available
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([payload], { type: 'application/json' })
      navigator.sendBeacon(url, blob)
      return
    }

    // Fallback to fetch if available
    if (typeof fetch === 'function') {
      // Do not await; swallow network errors
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true, // helps during unload in some browsers
      }).catch(() => {
        // Intentionally empty - swallow network errors
      })
      return
    }

    // Final fallback to axios if present
    if (axios !== undefined && typeof axios.post === 'function') {
      axios.post(url, payload, { headers: { 'Content-Type': 'application/json' } }).catch(() => {
        // Intentionally empty - swallow network errors
      })
    }
    // If no transport, silently no-op
  } catch (error_) {
    // Final safeguard: never throw
    try {
      console.error('[tempWindowFSRemoteLog] swallowed error', error_)
    } catch {
      /* noop */
    }
  }
}

/* eslint-disable @typescript-eslint/no-explicit-any, no-bitwise, func-names, @babel/no-unused-expressions, prettier/prettier */

// Functions to handle circular JSON structures taken from: https://github.com/isaacs/json-stringify-safe/blob/02cfafd45f06d076ac4bf0dd28be6738a07a72f9/stringify.js
export function stringify(
  obj: unknown,
  replacer?: (key: string, value: any) => any,
  spaces?: string | number,
  cycleReplacer?: (key: string, value: any) => any
): string {
  return JSON.stringify(obj, serializer(replacer, cycleReplacer), spaces)
}

function serializer(replacer?: (key: string, value: any) => any, cycleReplacer?: (key: string, value: any) => any) {
  const stack: any[] = []
  const keys: string[] = []

  cycleReplacer ??= function (_key: string, value: any) {
    if (stack[0] === value) return '[Circular ~]'
    return `[Circular ~.${keys.slice(0, stack.indexOf(value)).join('.')}]`
  }

  return function (this: any, key: string, value: any) {
    if (value instanceof Error) {
      value = stringifyErrorObj(value)
    }

    if (stack.length > 0) {
      const thisPos = stack.indexOf(this)
      ~thisPos ? stack.splice(thisPos + 1) : stack.push(this)
      ~thisPos ? keys.splice(thisPos, Infinity, key) : keys.push(key)
      if (~stack.indexOf(value)) value = cycleReplacer.call(this, key, value)
    } else stack.push(value)

    return replacer == null ? value : replacer.call(this, key, value)
  }
}

/**
 * Stringify Error objects. The properties by default are not enumerable and won't be stringified.
 * @param value - Error instance
 */
function stringifyErrorObj(value: Error): Record<string, unknown> {
  const error: Record<string, unknown> = {}
  Object.getOwnPropertyNames(value).forEach((key) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    error[key] = (value as any)[key]
  })
  return error
}

/* eslint-enable */
